import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './TitleBar.css';
import { FileMenu, EditMenu, ViewMenu, RunMenu, TerminalMenu, HelpMenu } from './Menu';
import { UserAvatar } from '../Auth/UserAvatar';
import { useAuth } from '../../contexts/AuthContext';
import { useCollab } from '../../contexts/CollabContext';
import { useGlobalContext } from '../../contexts/GlobalContext';
import { Mode } from '../../types/modes';
import AlertModal, { AlertType } from '../AlertModal/AlertModal';

interface MenuItem {
  label?: string;
  shortcut?: string;
  onClick?: () => void;
  disabled?: boolean;
  submenu?: MenuItem[];
  divider?: boolean;
}

interface TitleBarProps {
  onMinimize: () => void;
  onMaximize: () => void;
  onClose: () => void;
  onOpenSettings: () => void;
  onOpenFolder: () => void;
  onNewFile?: () => void;
  onNewFolder?: () => void;
  onRefresh?: () => void;
  onCollapseAll?: () => void;
  onSave?: () => void;
  onLoginClick: () => void;
  onRegisterClick: () => void;
}

const TitleBar: React.FC<TitleBarProps> = ({
  onMinimize, onMaximize, onClose, onOpenSettings, onOpenFolder, onNewFile, onNewFolder, onRefresh, onCollapseAll, onSave, onLoginClick, onRegisterClick
}) => {
  const { t } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const collab = useCollab();
  const { mode, projectRootPath } = useGlobalContext();
  const [fileMenuOpen, setFileMenuOpen] = useState(false);
  const fileMenuRef = useRef<HTMLDivElement>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [runServerRunning, setRunServerRunning] = useState(false);
  const [runServerUrl, setRunServerUrl] = useState<string | null>(null);
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '' as string | React.ReactNode,
    type: 'info' as AlertType,
  });
  
  // Check if running in Electron
  const isElectron = typeof window !== 'undefined' && window.electron !== undefined;
  
  const editMenuItems: MenuItem[] = [
    { label: 'Undo', shortcut: 'Ctrl+Z', onClick: () => {} },
    { label: 'Redo', shortcut: 'Ctrl+Y', onClick: () => {} },
    { divider: true },
    { label: 'Cut', shortcut: 'Ctrl+X', onClick: () => {} },
    { label: 'Copy', shortcut: 'Ctrl+C', onClick: () => {} },
    { label: 'Paste', shortcut: 'Ctrl+V', onClick: () => {} },
  ];
  const viewMenuItems: MenuItem[] = [
    { label: 'Appearance', submenu: [ { label: 'Full Screen', onClick: () => {} } ] },
    { label: 'Zoom In', shortcut: 'Ctrl++', onClick: () => {} },
    { label: 'Zoom Out', shortcut: 'Ctrl+-', onClick: () => {} },
  ];
  const [editMenuOpen, setEditMenuOpen] = useState(false);
  const editMenuRef = useRef<HTMLDivElement>(null);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const viewMenuRef = useRef<HTMLDivElement>(null);
  const [runMenuOpen, setRunMenuOpen] = useState(false);
  const runMenuRef = useRef<HTMLDivElement>(null);
  const [terminalMenuOpen, setTerminalMenuOpen] = useState(false);
  const terminalMenuRef = useRef<HTMLDivElement>(null);
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);
  const helpMenuRef = useRef<HTMLDivElement>(null);

  // Run Menu Handlers
  const showAlert = (title: string, message: string | React.ReactNode, type: AlertType = 'info') => {
    setAlertModal({ isOpen: true, title, message, type });
  };

  const closeAlert = () => {
    setAlertModal({ ...alertModal, isOpen: false });
  };

  const handleRunServer = async () => {
    if (!isElectron || !projectRootPath) {
      showAlert('No Project', 'Please open a project folder first', 'warning');
      return;
    }

    if (mode !== Mode.Local && mode !== Mode.Host) {
      showAlert('Mode Not Supported', 'Run is only available in Local and Host modes', 'warning');
      return;
    }

    try {
      const electronAPI = (window as any).electron;
      if (!electronAPI) {
        showAlert('Error', 'Electron API not available', 'error');
        return;
      }

      const result = await electronAPI.ipcRenderer.invoke('start-run-server', {
        projectPath: projectRootPath,
        projectType: 'auto'
      });

      if (result.success) {
        setRunServerRunning(true);
        setRunServerUrl(result.url);
        await electronAPI.ipcRenderer.invoke('open-external-url', result.url);
      } else if (result.isFrameworkProject) {
        showAlert('Framework Project Detected', (
          <div>
            <p>This project requires a development server.</p>
            <div className="command">
              npm run {result.command.split('npm run ')[1]}
            </div>
            <p>Please run the command above in the Terminal, then use <strong>"Open Custom URL..."</strong> to open your dev server.</p>
          </div>
        ), 'info');
      } else {
        showAlert('Failed to Start Server', result.error, 'error');
      }
    } catch (error) {
      console.error('Error starting server:', error);
      showAlert('Error', `Failed to start server: ${error}`, 'error');
    }
  };

  const handleStopServer = async () => {
    if (!isElectron) return;

    try {
      const electronAPI = (window as any).electron;
      if (!electronAPI) {
        showAlert('Error', 'Electron API not available', 'error');
        return;
      }

      const result = await electronAPI.ipcRenderer.invoke('stop-run-server');
      if (result.success) {
        setRunServerRunning(false);
        setRunServerUrl(null);
      }
    } catch (error) {
      console.error('Error stopping server:', error);
      showAlert('Error', `Failed to stop server: ${error}`, 'error');
    }
  };

  const handleOpenInBrowser = async () => {
    if (!runServerUrl) {
      showAlert('Server Not Running', 'Please start the server first using F5.', 'warning');
      return;
    }

    if (!isElectron) {
      showAlert('Error', 'This feature is only available in Electron', 'error');
      return;
    }

    try {
      const electronAPI = (window as any).electron;
      if (!electronAPI) {
        showAlert('Error', 'Electron API not available', 'error');
        return;
      }
      await electronAPI.ipcRenderer.invoke('open-external-url', runServerUrl);
    } catch (error) {
      console.error('Error opening browser:', error);
      showAlert('Error', `Failed to open browser: ${error}`, 'error');
    }
  };

  const handleOpenCustomURL = async () => {
    if (!isElectron) {
      showAlert('Error', 'This feature is only available in Electron', 'error');
      return;
    }

    const url = prompt('Enter server URL:', 'http://localhost:3000');
    if (url) {
      try {
        const electronAPI = (window as any).electron;
        if (!electronAPI) {
          showAlert('Error', 'Electron API not available', 'error');
          return;
        }
        await electronAPI.ipcRenderer.invoke('open-external-url', url);
      } catch (error) {
        console.error('Error opening custom URL:', error);
        showAlert('Error', `Failed to open URL: ${error}`, 'error');
      }
    }
  };

  const isRunAvailable = mode === Mode.Local || mode === Mode.Host;

  const runMenuItems: MenuItem[] = [
    // { label: 'Run', shortcut: 'F5', onClick: () => {} },
    // { label: 'Debug', shortcut: 'F6', onClick: () => {} },
    { 
      label: 'Run Server', 
      shortcut: 'F5', 
      onClick: handleRunServer,
      disabled: !isRunAvailable || !projectRootPath || runServerRunning
    },
    { 
      label: 'Stop Server', 
      shortcut: 'Shift+F5', 
      onClick: handleStopServer,
      disabled: !runServerRunning
    },
    { divider: true },
    { 
      label: 'Open in Browser', 
      onClick: handleOpenInBrowser,
      disabled: !runServerRunning || !runServerUrl
    },
    { 
      label: 'Open Custom URL...', 
      onClick: handleOpenCustomURL,
      disabled: !isRunAvailable
    },
  ];
  const terminalMenuItems: MenuItem[] = [
    { label: 'New Terminal', onClick: () => {} },
    { label: 'Split Terminal', onClick: () => {} },
  ];
  const helpMenuItems: MenuItem[] = [
    { label: 'Documentation', onClick: () => {} },
    { label: 'About', onClick: () => {} },
  ];

  // Menu handlers
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (onSave) onSave();
      }
      // F5 - Run Server
      if (e.key === 'F5' && !e.shiftKey) {
        e.preventDefault();
        if (isRunAvailable && projectRootPath && !runServerRunning) {
          handleRunServer();
        }
      }
      // Shift+F5 - Stop Server
      if (e.key === 'F5' && e.shiftKey) {
        e.preventDefault();
        if (runServerRunning) {
          handleStopServer();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onSave, isRunAvailable, projectRootPath, runServerRunning]);

  const fileMenuItems: MenuItem[] = [
    { label: 'New Text File', shortcut: 'Ctrl+N', onClick: onNewFile },
    { label: 'New Window', shortcut: 'Ctrl+Shift+N', onClick: () => {} },
    { label: 'New Window with Profile', onClick: () => {} },
    { divider: true },
    { label: 'Open File...', shortcut: 'Ctrl+O', onClick: () => {} },
    { label: 'Open Folder...', shortcut: 'Ctrl+M Ctrl+O', onClick: onOpenFolder },
    { label: 'Open Workspace from File...', onClick: () => {} },
    { label: 'Open Recent', submenu: [ { label: 'Recent 1' }, { label: 'Recent 2' } ] },
    { divider: true },
    { label: 'Add Folder to Workspace...', onClick: () => {} },
    { label: 'Save Workspace As...', onClick: () => {} },
    { label: 'Duplicate Workspace', onClick: () => {} },
    { divider: true },
    { label: 'Save', shortcut: 'Ctrl+S', onClick: onSave },
    { label: 'Save As...', shortcut: 'Ctrl+Shift+S', onClick: () => {} },
    { label: 'Save All', shortcut: 'Ctrl+M S', disabled: true },
    { divider: true },
    { label: 'Share', submenu: [ { label: 'Share 1' }, { label: 'Share 2' } ] },
    { label: 'Auto Save', onClick: () => {} },
    { label: 'Preferences', submenu: [ { label: 'Settings' }, { label: 'Keymaps' } ] },
    { divider: true },
    { label: 'Revert File', onClick: () => {} },
    { label: 'Close Editor', shortcut: 'Ctrl+F4', onClick: () => {} },
    { label: 'Close Folder', shortcut: 'Ctrl+M F', onClick: () => {} },
    { label: 'Close Window', shortcut: 'Alt+F4', onClick: () => {} },
    { divider: true },
    { label: 'Exit', onClick: () => {} },
  ];

  // Menu close handlers
  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (fileMenuRef.current && !fileMenuRef.current.contains(e.target as Node)) {
        setFileMenuOpen(false);
      }
    };
    if (fileMenuOpen) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [fileMenuOpen]);

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (editMenuRef.current && !editMenuRef.current.contains(e.target as Node)) {
        setEditMenuOpen(false);
      }
    };
    if (editMenuOpen) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [editMenuOpen]);

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (viewMenuRef.current && !viewMenuRef.current.contains(e.target as Node)) {
        setViewMenuOpen(false);
      }
    };
    if (viewMenuOpen) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [viewMenuOpen]);

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (runMenuRef.current && !runMenuRef.current.contains(e.target as Node)) {
        setRunMenuOpen(false);
      }
    };
    if (runMenuOpen) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [runMenuOpen]);

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (terminalMenuRef.current && !terminalMenuRef.current.contains(e.target as Node)) {
        setTerminalMenuOpen(false);
      }
    };
    if (terminalMenuOpen) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [terminalMenuOpen]);

  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (helpMenuRef.current && !helpMenuRef.current.contains(e.target as Node)) {
        setHelpMenuOpen(false);
      }
    };
    if (helpMenuOpen) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [helpMenuOpen]);

  // Menü dışına tıklanınca kapat
  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (fileMenuRef.current && !fileMenuRef.current.contains(e.target as Node)) {
        setFileMenuOpen(false);
      }
    };
    if (fileMenuOpen) {
      document.addEventListener('mousedown', handleClick);
    } else {
      document.removeEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [fileMenuOpen]);

  const getInitials = (name: string): string => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getColorFromId = (id: string): string => {
    const colors = [
      '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3',
      '#00bcd4', '#009688', '#4caf50', '#ff9800', '#ff5722'
    ];
    const numId = parseInt(id, 10) || 0;
    return colors[numId % colors.length];
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleLogout = async () => {
    setIsUserMenuOpen(false);
    if (collab.sessionId) {
      await collab.leaveSession();
    }
    logout();
  };

  const handleLoginClick = () => {
    setIsUserMenuOpen(false);
    onLoginClick();
  };

  const handleRegisterClick = () => {
    setIsUserMenuOpen(false);
    onRegisterClick();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const renderMenu = (items: MenuItem[], level = 0) => (
    <div className="dropdown-menu" style={level > 0 ? { left: '100%', top: 0, minWidth: 180 } : {}}>
      {items.map((item, idx) =>
        item.divider ? (
          <div key={idx} style={{ borderTop: '1px solid #444', margin: '4px 0' }} />
        ) : (
          <div
            key={(item.label || '') + idx}
            className={`dropdown-item${item.disabled ? ' disabled' : ''}`}
            onClick={item.disabled ? undefined : item.onClick}
            style={{ opacity: item.disabled ? 0.5 : 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}
            onMouseEnter={e => {
              if (item.submenu) {
                (e.currentTarget as HTMLElement).classList.add('submenu-open');
              }
            }}
            onMouseLeave={e => {
              if (item.submenu) {
                (e.currentTarget as HTMLElement).classList.remove('submenu-open');
              }
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {item.label}
              {item.submenu && <span style={{ marginLeft: 8 }}>{'>'}</span>}
            </span>
            {item.shortcut && <span style={{ color: '#888', marginLeft: 16, fontSize: 12 }}>{item.shortcut}</span>}
            {item.submenu && (
              <div className="dropdown-menu submenu" style={{ display: 'none', position: 'absolute', left: '100%', top: 0, minWidth: 180 }}>
                {renderMenu(item.submenu, level + 1)}
              </div>
            )}
          </div>
        )
      )}
    </div>
  );

  return (
    <div className="title-bar">
      <div className="title-bar-left">
        <div className="app-icon">
          <img src="/icon.png" alt="Intellium Logo" />
        </div>
        {isElectron && (
          <div className="menu-items">
            <div className="menu-item" ref={fileMenuRef} style={{ position: 'relative' }}>
              <span onClick={() => setFileMenuOpen(open => !open)}>{t('menuItems.file')}</span>
              {fileMenuOpen && <FileMenu items={fileMenuItems} />}
            </div>
            <div className="menu-item" ref={editMenuRef} style={{ position: 'relative' }}>
              <span onClick={() => setEditMenuOpen(open => !open)}>{t('menuItems.edit')}</span>
              {editMenuOpen && <EditMenu items={editMenuItems} />}
            </div>
            <div className="menu-item" ref={viewMenuRef} style={{ position: 'relative' }}>
              <span onClick={() => setViewMenuOpen(open => !open)}>{t('menuItems.view')}</span>
              {viewMenuOpen && <ViewMenu items={viewMenuItems} />}
            </div>
            <div className="menu-item" ref={runMenuRef} style={{ position: 'relative' }}>
              <span onClick={() => setRunMenuOpen(open => !open)}>{t('menuItems.run')}</span>
              {runMenuOpen && <RunMenu items={runMenuItems} />}
            </div>
            <div className="menu-item" ref={terminalMenuRef} style={{ position: 'relative' }}>
              <span onClick={() => setTerminalMenuOpen(open => !open)}>{t('menuItems.terminal')}</span>
              {terminalMenuOpen && <TerminalMenu items={terminalMenuItems} />}
            </div>
            <div className="menu-item" ref={helpMenuRef} style={{ position: 'relative' }}>
              <span onClick={() => setHelpMenuOpen(open => !open)}>{t('menuItems.help')}</span>
              {helpMenuOpen && <HelpMenu items={helpMenuItems} />}
            </div>
            <span className="menu-item" onClick={onOpenSettings}>{t('menuItems.settings')}</span>
          </div>
        )}
      </div>
      <div className="title-bar-right">
        <div className="title-bar-avatar" ref={userMenuRef}>
          <button 
            className="title-bar-user-button" 
            onClick={toggleUserMenu}
            title={user ? user.name : 'Account'}
          >
            {user ? (
              <div
                className="title-bar-user-initials"
                style={{ backgroundColor: getColorFromId(user.id) }}
              >
                {getInitials(user.name)}
              </div>
            ) : (
              <div className="title-bar-user-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm0 1c-2.7 0-8 1.35-8 4v1h16v-1c0-2.65-5.3-4-8-4z" />
                </svg>
              </div>
            )}
          </button>

          {isUserMenuOpen && (
            <div className="title-bar-user-menu">
              {user ? (
                <>
                  <div className="title-bar-user-menu-header">
                    <div
                      className="title-bar-user-menu-avatar"
                      style={{ backgroundColor: getColorFromId(user.id) }}
                    >
                      {getInitials(user.name)}
                    </div>
                    <div className="title-bar-user-menu-info">
                      <div className="title-bar-user-menu-name">{user.name}</div>
                      <div className="title-bar-user-menu-email">{user.email}</div>
                    </div>
                  </div>
                  <div className="title-bar-user-menu-divider"></div>
                  <button className="title-bar-user-menu-item" onClick={handleLogout}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M11 8a.5.5 0 0 1-.5.5H6.707l1.147 1.146a.5.5 0 0 1-.708.708l-2-2a.5.5 0 0 1 0-.708l2-2a.5.5 0 0 1 .708.708L6.707 7.5H10.5A.5.5 0 0 1 11 8z"/>
                      <path d="M10 3.5a.5.5 0 0 0-.5-.5h-8a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 1 1 0v2A1.5 1.5 0 0 1 9.5 14h-8A1.5 1.5 0 0 1 0 12.5v-9A1.5 1.5 0 0 1 1.5 2h8A1.5 1.5 0 0 1 11 3.5v2a.5.5 0 0 1-1 0v-2z"/>
                    </svg>
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button className="title-bar-user-menu-item" onClick={handleLoginClick}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M11 8a.5.5 0 0 1-.5.5H6.707l1.147 1.146a.5.5 0 0 1-.708.708l-2-2a.5.5 0 0 1 0-.708l2-2a.5.5 0 0 1 .708.708L6.707 7.5H10.5A.5.5 0 0 1 11 8z"/>
                      <path d="M10 3.5a.5.5 0 0 0-.5-.5h-8a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 1 1 0v2A1.5 1.5 0 0 1 9.5 14h-8A1.5 1.5 0 0 1 0 12.5v-9A1.5 1.5 0 0 1 1.5 2h8A1.5 1.5 0 0 1 11 3.5v2a.5.5 0 0 1-1 0v-2z"/>
                    </svg>
                    Login
                  </button>
                  <button className="title-bar-user-menu-item" onClick={handleRegisterClick}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
                      <path fillRule="evenodd" d="M13.5 5a.5.5 0 0 1 .5.5V7h1.5a.5.5 0 0 1 0 1H14v1.5a.5.5 0 0 1-1 0V8h-1.5a.5.5 0 0 1 0-1H13V5.5a.5.5 0 0 1 .5-.5z"/>
                    </svg>
                    Register
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        {isElectron && (
          <>
            <button className="window-control minimize" onClick={onMinimize}>
              &#x2014;
            </button>
            <button className="window-control maximize" onClick={onMaximize}>
              &#x2610;
            </button>
            <button className="window-control close" onClick={onClose}>
              &#x2715;
            </button>
          </>
        )}
      </div>
      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={closeAlert}
      />
    </div>
  );
};

export default TitleBar; 